import { areObjectIdsEqual } from 'helpers/idComparison';
import { isSyncStatusPendingDelete, isSyncStatusSuccess } from 'shared/domain/entitySyncStatus/syncStatus';
import { isUploadStatusSuccess } from 'shared/types/uploadStatus';
import type {
  DocumentationEntity,
  DocumentationInsertEntity,
  VersionEntity,
} from 'serviceWorker/repository/documentation/entity';

type StoredVersion = Pick<VersionEntity, 'localData' | 'uploadStatus'>;
type StoredDocumentation = Pick<DocumentationEntity, 'syncStatus'> & {
  versions: (StoredVersion & Pick<VersionEntity, 'syncStatus'>)[];
};

/** A file picked on this device that the backend has not stored yet: only this row holds it. */
export function isUnsentVersion(version: StoredVersion): boolean {
  return Boolean(version.localData) && !isUploadStatusSuccess(version.uploadStatus);
}

/**
 * Whether a change of the documentation still waits in the queue (its upload,
 * an edit, a deletion): the queued request finds it only in this row, by its
 * local id. Settled when it and every version are synced, as documents are.
 */
export function hasChangesToSend(documentation: StoredDocumentation): boolean {
  return (
    !isSyncStatusSuccess(documentation.syncStatus) ||
    documentation.versions.some((version) => isUnsentVersion(version) || !isSyncStatusSuccess(version.syncStatus))
  );
}

/**
 * The versions to store when the backend's copy of a documentation replaces
 * the stored one (PT-7117: a documentation has many versions): each version
 * keeps the file picked on this device until its upload is confirmed and
 * synced, and a version still being uploaded stays even before the backend
 * lists it.
 */
export function mergeStoredVersions(
  incoming: DocumentationInsertEntity,
  existing: DocumentationEntity
): DocumentationInsertEntity {
  const isExistingSynced = isSyncStatusSuccess(existing.syncStatus);
  const incomingIds = new Set(incoming.versions.map(({ _id }) => _id));

  const versions = incoming.versions.map((version) => {
    // The backend's versions all carry an id, so an unsent stored one never matches.
    const stored = existing.versions.find(({ _id }) => areObjectIdsEqual(_id, version._id));
    if (!stored?.localData) {
      return version;
    }
    const isUploaded =
      isExistingSynced && isSyncStatusSuccess(stored.syncStatus) && isUploadStatusSuccess(version.uploadStatus);
    return { ...version, localData: isUploaded ? undefined : stored.localData };
  });
  const notListedYet = existing.versions.filter((version) => isUnsentVersion(version) && !incomingIds.has(version._id));

  return {
    ...incoming,
    versions: [...versions, ...notListedYet],
    // A delete still waiting in the queue keeps the documentation hidden.
    deleted: isSyncStatusPendingDelete(existing.syncStatus) ? 1 : incoming.deleted,
  };
}
